import { lazy } from 'react';
import { Navigate, Outlet } from 'react-router-dom';

// project imports
import ErrorBoundary from './ErrorBoundary';
import Loadable from 'components/Loadable';
import DashboardLayout from 'layout/Dashboard';
import PagesLayout from 'layout/Pages';
import SimpleLayout from 'layout/Simple';
import RoleGuard from 'utils/route-guard/RoleGuard';
import { StudentRegistrationProvider } from 'contexts/StudentRegistrationContext';
import HydratedReadOnlyForm from 'sections/therapist/child-detail/HydratedReadOnlyForm';
import IepVersionEditor from 'pages/shared/iep-version-editor';

import { SimpleLayoutType } from 'config';
import { loader as productsLoader, productLoader } from 'api/products';

// render - dashboard
const DashboardDefault = Loadable(lazy(() => import('pages/dashboard/default')));
const DashboardAnalytics = Loadable(lazy(() => import('pages/dashboard/analytics')));

// render - therapist
const TherapistDashboard = Loadable(lazy(() => import('pages/therapist/dashboard')));
const TherapistCalendar = Loadable(lazy(() => import('pages/therapist/calendar')));
const TherapistAttendance = Loadable(lazy(() => import('pages/therapist/attendance')));
const TherapistChild = Loadable(lazy(() => import('pages/therapist/configuration')));
const TherapistSettings = Loadable(lazy(() => import('pages/therapist/settings')));
const TherapistChildDetail = Loadable(lazy(() => import('pages/therapist/child-detail')));
const TherapistDataCollection = Loadable(lazy(() => import('pages/therapist/data-collection')));
const TherapistSyncStatus = Loadable(lazy(() => import('pages/therapist/sync-status')));
const TherapistDailyMandV1 = Loadable(lazy(() => import('pages/therapist/daily-mand-v1')));
const TherapistDailyMandV2 = Loadable(lazy(() => import('pages/therapist/daily-mand-v2')));
// Hub is no longer used - data-collection now handles both direct access and session-based access
// const TherapistDataCollectionHub = Loadable(lazy(() => import('pages/therapist/data-collection-hub')));

// render - inclusionhead
const InclusionHeadDashboard = Loadable(lazy(() => import('pages/inclusionhead/dashboard')));
const InclusionHeadChildProfiles = Loadable(lazy(() => import('pages/inclusionhead/child-profiles')));
const InclusionHeadRegistration = Loadable(lazy(() => import('pages/inclusionhead/student-registration')));
const InclusionHeadEnquiryForm = Loadable(lazy(() => import('pages/inclusionhead/enquiry-form')));
const InclusionHeadIntakeForm = Loadable(lazy(() => import('pages/inclusionhead/intake-form')));
const InclusionHeadIEP = Loadable(lazy(() => import('pages/inclusionhead/iep')));
const InclusionHeadPlacementAssessment = Loadable(lazy(() => import('pages/inclusionhead/placement-assessment')));
const InclusionHeadPlacementInteraction = Loadable(lazy(() => import('pages/inclusionhead/placement-interaction')));
const InclusionHeadSessionNotes = Loadable(lazy(() => import('pages/inclusionhead/session-notes')));
const InclusionHeadAbcAnalysis = Loadable(lazy(() => import('pages/inclusionhead/abc-analysis')));
const InclusionHeadCounsellorObservations = Loadable(lazy(() => import('pages/inclusionhead/counsellor-observations')));
const InclusionHeadParentTherapistMeeting = Loadable(lazy(() => import('pages/inclusionhead/parent-therapist-meeting')));
const InclusionHeadAnecdotalRecord = Loadable(lazy(() => import('pages/inclusionhead/anecdotal-record')));
const InclusionHeadStudentReferral = Loadable(lazy(() => import('pages/inclusionhead/student-referral')));
const InclusionHeadPlacementReport = Loadable(lazy(() => import('pages/inclusionhead/placement-assessment-report')));
const InclusionHeadDataCollection = Loadable(lazy(() => import('pages/inclusionhead/data-collection')));
const SupervisorDataCollection = Loadable(lazy(() => import('pages/supervisor/data-collection')));
const InclusionHeadMyCalendar = Loadable(lazy(() => import('pages/inclusionhead/my-calendar')));
const InclusionHeadWorkflowRegistration = Loadable(lazy(() => import('pages/inclusionhead/workflow-registration')));
const InclusionHeadWorkflowForm = Loadable(lazy(() => import('pages/inclusionhead/workflow-form')));
const InclusionHeadWorkflowComponentIEP = Loadable(lazy(() => import('pages/inclusionhead/workflow-component-iep')));
const InclusionHeadWorkflowChildDetail = Loadable(lazy(() => import('pages/inclusionhead/workflow-child-detail')));
const InclusionHeadWorkflowSubmissionView = Loadable(lazy(() => import('pages/inclusionhead/workflow-submission-view')));

// render - admin
const AdminDashboard = Loadable(lazy(() => import('pages/admin/dashboard')));
const AdminUnderDevelopment = Loadable(lazy(() => import('pages/admin/under-development')));
const AdminOrganizationsCenters = Loadable(lazy(() => import('pages/admin/organizations-centers')));
const AdminFeaturesPages = Loadable(lazy(() => import('pages/admin/features-pages')));
const AdminAccessMatrix = Loadable(lazy(() => import('pages/admin/access-matrix')));
const AdminRoleDefinitions = Loadable(lazy(() => import('pages/admin/role-definitions')));
const AdminWorkflowBuilder = Loadable(lazy(() => import('pages/admin/workflow-builder')));
const AdminComponents = Loadable(lazy(() => import('pages/admin/components')));
const AdminUserManagement = Loadable(lazy(() => import('pages/admin/user-management')));
const AdminAddUser = Loadable(lazy(() => import('pages/admin/add-user')));
const AdminProductTiers = Loadable(lazy(() => import('pages/admin/product-tiers')));
const AdminFormPage = Loadable(lazy(() => import('pages/admin/form-page')));
const AdminCenterTimeSessionNotes = Loadable(lazy(() => import('pages/admin/center-time-session-notes')));
const AdminAdolescenceSessionNotes = Loadable(lazy(() => import('pages/admin/adolescence-session-notes')));
const AdminSelectCenter = Loadable(lazy(() => import('pages/admin/pages/select-center')));
const AdminSelectProgram = Loadable(lazy(() => import('pages/admin/pages/select-program')));
const AdminSelectGrade = Loadable(lazy(() => import('pages/admin/pages/select-grade')));
const AdminComingSoon = Loadable(lazy(() => import('pages/admin/pages/coming-soon')));
const AdminLearningSupportSessionSummary = Loadable(lazy(() => import('pages/admin/learning-support-session-summary')));

// render - caregiver
const CaregiverDashboard = Loadable(lazy(() => import('pages/caregiver/dashboard')));
const CaregiverChildren = Loadable(lazy(() => import('pages/caregiver/child')));
const CaregiverChildDetail = Loadable(lazy(() => import('pages/caregiver/child-detail')));
const CaregiverCommunicationDiary = Loadable(lazy(() => import('pages/caregiver/communication-diary')));
// Placeholder for the caregiver's Activity Center, which is in the menu (gated by the
// activity_center grant) but not built yet — the same shared "Coming Soon" screen the
// admin and clinical-head personas use for their pending items.
const CaregiverComingSoon = Loadable(lazy(() => import('pages/admin/pages/coming-soon')));

// render - clinicalhead
const ClinicalHeadDashboard = Loadable(lazy(() => import('pages/clinicalhead/dashboard')));
const ClinicalHeadComingSoon = Loadable(lazy(() => import('pages/admin/pages/coming-soon')));

// render - supervisor
const SupervisorDashboard = Loadable(lazy(() => import('pages/supervisor/dashboard')));
const SupervisorCalendar = Loadable(lazy(() => import('pages/supervisor/calendar')));
const SupervisorAttendance = Loadable(lazy(() => import('pages/supervisor/attendance')));
const SupervisorChild = Loadable(lazy(() => import('pages/supervisor/configuration')));
const SupervisorChildDetail = Loadable(lazy(() => import('pages/supervisor/child-detail')));
const SupervisorAddStudent = Loadable(lazy(() => import('pages/supervisor/add-student')));
const SupervisorDynamicForm = Loadable(lazy(() => import('pages/supervisor/dynamic-form')));
const SupervisorSettings = Loadable(lazy(() => import('pages/supervisor/settings')));
const SupervisorReports = Loadable(lazy(() => import('pages/supervisor/reports')));
const SupervisorTherapists = Loadable(lazy(() => import('pages/supervisor/therapists')));
const SupervisorSupervisors = Loadable(lazy(() => import('pages/supervisor/supervisors')));
const SupervisorSupervisorDetail = Loadable(lazy(() => import('pages/supervisor/supervisor-detail')));
const SupervisorTherapistDetail = Loadable(lazy(() => import('pages/supervisor/therapist-detail')));
const SupervisorSkills = Loadable(lazy(() => import('pages/supervisor/skills')));
const SupervisorMaterials = Loadable(lazy(() => import('pages/supervisor/material-repository')));
const SupervisorFormBuilder = Loadable(lazy(() => import('pages/supervisor/form-builder')));

// render - widget
const WidgetStatistics = Loadable(lazy(() => import('pages/widget/statistics')));
const WidgetData = Loadable(lazy(() => import('pages/widget/data')));
const WidgetChart = Loadable(lazy(() => import('pages/widget/chart')));

// render - applications
const AppChat = Loadable(lazy(() => import('pages/apps/chat')));
const AppCalendar = Loadable(lazy(() => import('pages/apps/calendar')));

const AppKanban = Loadable(lazy(() => import('pages/apps/kanban')));
const AppKanbanBacklogs = Loadable(lazy(() => import('sections/apps/kanban/Backlogs')));
const AppKanbanBoard = Loadable(lazy(() => import('sections/apps/kanban/Board')));

const AppCustomerList = Loadable(lazy(() => import('pages/apps/customer/list')));
const AppCustomerCard = Loadable(lazy(() => import('pages/apps/customer/card')));

const AppInvoiceCreate = Loadable(lazy(() => import('pages/apps/invoice/create')));
const AppInvoiceDashboard = Loadable(lazy(() => import('pages/apps/invoice/dashboard')));
const AppInvoiceList = Loadable(lazy(() => import('pages/apps/invoice/list')));
const AppInvoiceDetails = Loadable(lazy(() => import('pages/apps/invoice/details')));
const AppInvoiceEdit = Loadable(lazy(() => import('pages/apps/invoice/edit')));

const UserProfile = Loadable(lazy(() => import('pages/apps/profiles/user')));
const UserTabPersonal = Loadable(lazy(() => import('sections/apps/profiles/user/TabPersonal')));
const UserTabPayment = Loadable(lazy(() => import('sections/apps/profiles/user/TabPayment')));
const UserTabPassword = Loadable(lazy(() => import('sections/apps/profiles/user/TabPassword')));
const UserTabSettings = Loadable(lazy(() => import('sections/apps/profiles/user/TabSettings')));

const AccountProfile = Loadable(lazy(() => import('pages/apps/profiles/account')));
const AccountTabProfile = Loadable(lazy(() => import('sections/apps/profiles/account/TabProfile')));
const AccountTabPersonal = Loadable(lazy(() => import('sections/apps/profiles/account/TabPersonal')));
const AccountTabAccount = Loadable(lazy(() => import('sections/apps/profiles/account/TabAccount')));
const AccountTabPassword = Loadable(lazy(() => import('sections/apps/profiles/account/TabPassword')));
const AccountTabRole = Loadable(lazy(() => import('sections/apps/profiles/account/TabRole')));
const AccountTabSettings = Loadable(lazy(() => import('sections/apps/profiles/account/TabSettings')));

const AppECommProducts = Loadable(lazy(() => import('pages/apps/e-commerce/products')));
const AppECommProductDetails = Loadable(lazy(() => import('pages/apps/e-commerce/product-details')));
const AppECommProductList = Loadable(lazy(() => import('pages/apps/e-commerce/products-list')));
const AppECommCheckout = Loadable(lazy(() => import('pages/apps/e-commerce/checkout')));
const AppECommAddProduct = Loadable(lazy(() => import('pages/apps/e-commerce/add-product')));

// render - forms & tables
const FormsValidation = Loadable(lazy(() => import('pages/forms/validation')));
const FormsWizard = Loadable(lazy(() => import('pages/forms/wizard')));

const FormsLayoutBasic = Loadable(lazy(() => import('pages/forms/layouts/basic')));
const FormsLayoutMultiColumn = Loadable(lazy(() => import('pages/forms/layouts/multi-column')));
const FormsLayoutActionBar = Loadable(lazy(() => import('pages/forms/layouts/action-bar')));
const FormsLayoutStickyBar = Loadable(lazy(() => import('pages/forms/layouts/sticky-bar')));

const FormsPluginsMask = Loadable(lazy(() => import('pages/forms/plugins/mask')));
const FormsPluginsClipboard = Loadable(lazy(() => import('pages/forms/plugins/clipboard')));
const FormsPluginsRecaptcha = Loadable(lazy(() => import('pages/forms/plugins/re-captcha')));
const FormsPluginsEditor = Loadable(lazy(() => import('pages/forms/plugins/editor')));
const FormsPluginsDropzone = Loadable(lazy(() => import('pages/forms/plugins/dropzone')));

const ReactTableBasic = Loadable(lazy(() => import('pages/tables/react-table/basic')));
const ReactDenseTable = Loadable(lazy(() => import('pages/tables/react-table/dense')));
const ReactTableSorting = Loadable(lazy(() => import('pages/tables/react-table/sorting')));
const ReactTableFiltering = Loadable(lazy(() => import('pages/tables/react-table/filtering')));
const ReactTableGrouping = Loadable(lazy(() => import('pages/tables/react-table/grouping')));
const ReactTablePagination = Loadable(lazy(() => import('pages/tables/react-table/pagination')));
const ReactTableRowSelection = Loadable(lazy(() => import('pages/tables/react-table/row-selection')));
const ReactTableExpanding = Loadable(lazy(() => import('pages/tables/react-table/expanding')));
const ReactTableEditable = Loadable(lazy(() => import('pages/tables/react-table/editable')));
const ReactTableDragDrop = Loadable(lazy(() => import('pages/tables/react-table/drag-drop')));
const ReactTableColumnVisibility = Loadable(lazy(() => import('pages/tables/react-table/column-visibility')));
const ReactTableColumnResizing = Loadable(lazy(() => import('pages/tables/react-table/column-resizing')));
const ReactTableStickyTable = Loadable(lazy(() => import('pages/tables/react-table/sticky')));
const ReactTableUmbrella = Loadable(lazy(() => import('pages/tables/react-table/umbrella')));
const ReactTableEmpty = Loadable(lazy(() => import('pages/tables/react-table/empty')));
const ReactTableVirtualized = Loadable(lazy(() => import('pages/tables/react-table/virtualized')));

// render - charts & map
const ChartApexchart = Loadable(lazy(() => import('pages/charts/apexchart')));
const ChartOrganization = Loadable(lazy(() => import('pages/charts/org-chart')));
const Map = Loadable(lazy(() => import('pages/map')));

// table routing
const MuiTableBasic = Loadable(lazy(() => import('pages/tables/mui-table/basic')));
const MuiTableDense = Loadable(lazy(() => import('pages/tables/mui-table/dense')));
const MuiTableEnhanced = Loadable(lazy(() => import('pages/tables/mui-table/enhanced')));
const MuiTableDatatable = Loadable(lazy(() => import('pages/tables/mui-table/datatable')));
const MuiTableCustom = Loadable(lazy(() => import('pages/tables/mui-table/custom')));
const MuiTableFixedHeader = Loadable(lazy(() => import('pages/tables/mui-table/fixed-header')));
const MuiTableCollapse = Loadable(lazy(() => import('pages/tables/mui-table/collapse')));

// pages routing
const AuthLogin = Loadable(lazy(() => import('pages/auth/jwt/login')));
const AuthRegister = Loadable(lazy(() => import('pages/auth/jwt/register')));
const AuthForgotPassword = Loadable(lazy(() => import('pages/auth/jwt/forgot-password')));
const AuthResetPassword = Loadable(lazy(() => import('pages/auth/jwt/reset-password')));
const AuthCheckMail = Loadable(lazy(() => import('pages/auth/jwt/check-mail')));
const AuthCodeVerification = Loadable(lazy(() => import('pages/auth/jwt/code-verification')));

const MaintenanceError = Loadable(lazy(() => import('pages/maintenance/404')));
const MaintenanceError500 = Loadable(lazy(() => import('pages/maintenance/500')));
const MaintenanceUnderConstruction = Loadable(lazy(() => import('pages/maintenance/under-construction')));
const MaintenanceComingSoon = Loadable(lazy(() => import('pages/maintenance/coming-soon')));

const AppContactUS = Loadable(lazy(() => import('pages/contact-us')));

// render - sample page
const SamplePage = Loadable(lazy(() => import('pages/extra-pages/sample-page')));
const PricingPage = Loadable(lazy(() => import('pages/extra-pages/pricing')));

// ==============================|| MAIN ROUTING ||============================== //

const MainRoutes = {
  path: '/',
  // Catch errors from any route below (failed lazy-chunk loads, loader/render
  // errors, 404s) and show a friendly page instead of React Router's dev screen.
  errorElement: <ErrorBoundary />,
  children: [
    {
      path: '/',
      element: <DashboardLayout />,
      children: [
        {
          path: 'dashboard',
          children: [
            {
              path: 'default',
              element: <DashboardDefault />
            },
            {
              path: 'analytics',
              element: <DashboardAnalytics />
            },
            {
              path: 'invoice',
              element: <AppInvoiceDashboard />
            }
          ]
        },
        {
          path: 'therapist',
          children: [
            {
              path: 'dashboard',
              element: (
                <RoleGuard allowedRoles={['therapist']}>
                  <TherapistDashboard />
                </RoleGuard>
              )
            },
            {
              path: 'calendar',
              element: (
                <RoleGuard allowedRoles={['therapist']}>
                  <TherapistCalendar />
                </RoleGuard>
              )
            },
            {
              path: 'attendance',
              element: (
                <RoleGuard allowedRoles={['therapist']}>
                  <TherapistAttendance />
                </RoleGuard>
              )
            },
            {
              path: 'child',
              element: (
                <RoleGuard allowedRoles={['therapist']}>
                  <TherapistChild />
                </RoleGuard>
              )
            },
            {
              path: 'settings',
              element: (
                <RoleGuard allowedRoles={['therapist']}>
                  <TherapistSettings />
                </RoleGuard>
              )
            },
            {
              path: 'child/:childId',
              element: (
                <RoleGuard allowedRoles={['therapist']}>
                  <TherapistChildDetail />
                </RoleGuard>
              )
            },
            {
              path: 'child/:childId/:tab',
              element: (
                <RoleGuard allowedRoles={['therapist']}>
                  <TherapistChildDetail />
                </RoleGuard>
              )
            },
            {
              path: 'data-collection',
              element: (
                <RoleGuard allowedRoles={['therapist']}>
                  <TherapistDataCollection />
                </RoleGuard>
              )
            },
            {
              path: 'data-collection/:childId',
              element: (
                <RoleGuard allowedRoles={['therapist']}>
                  <TherapistDataCollection />
                </RoleGuard>
              )
            },
            {
              path: 'data-collection/:childId/:type',
              element: (
                <RoleGuard allowedRoles={['therapist']}>
                  <TherapistDataCollection />
                </RoleGuard>
              )
            },
            {
              path: 'my-calendar',
              element: (
                <RoleGuard allowedRoles={['therapist']}>
                  <TherapistCalendar />
                </RoleGuard>
              )
            },
            {
              path: 'sync-status',
              element: (
                <RoleGuard allowedRoles={['therapist']}>
                  <TherapistSyncStatus />
                </RoleGuard>
              )
            },
            {
              path: 'skills',
              element: (
                <RoleGuard allowedRoles={['therapist']}>
                  <SupervisorSkills />
                </RoleGuard>
              )
            },
            // Shareable clinical pages, reachable when granted to the Therapist role (or a
            // role cloned from it) in Role Definitions. Same shared components the supervisor
            // uses; RoleGuard also honours the persona, so cloned roles pass too.
            {
              path: 'materials',
              element: (
                <RoleGuard allowedRoles={['therapist']}>
                  <SupervisorMaterials />
                </RoleGuard>
              )
            },
            {
              path: 'therapists',
              element: (
                <RoleGuard allowedRoles={['therapist']}>
                  <SupervisorTherapists />
                </RoleGuard>
              )
            },
            {
              path: 'therapists/:id',
              element: (
                <RoleGuard allowedRoles={['therapist']}>
                  <SupervisorTherapistDetail />
                </RoleGuard>
              )
            },
            {
              path: 'therapists/:id/:tab',
              element: (
                <RoleGuard allowedRoles={['therapist']}>
                  <SupervisorTherapistDetail />
                </RoleGuard>
              )
            },
            {
              path: 'daily-mand-v1',
              element: (
                <RoleGuard allowedRoles={['therapist']}>
                  <TherapistDailyMandV1 />
                </RoleGuard>
              )
            },
            {
              path: 'daily-mand-v2',
              element: (
                <RoleGuard allowedRoles={['therapist']}>
                  <TherapistDailyMandV2 />
                </RoleGuard>
              )
            },
            {
              // The inclusionhead form components (Enquiry / Intake / IEP) internally
              // call `useStudentRegistration()`, so they must render inside a
              // StudentRegistrationProvider. The therapist's read-only view also
              // needs the saved client data hydrated into that context, otherwise
              // the form opens blank — HydratedReadOnlyForm fetches and seeds it.
              path: 'child/:childId/enquiry',
              element: (
                <RoleGuard allowedRoles={['therapist']}>
                  <StudentRegistrationProvider>
                    <HydratedReadOnlyForm>
                      <InclusionHeadEnquiryForm />
                    </HydratedReadOnlyForm>
                  </StudentRegistrationProvider>
                </RoleGuard>
              )
            },
            {
              // No `merged`: the read-only view has to mirror what was actually
              // filled in. The Onboarding tab links Enquiry and Intake to two
              // separate viewers, so rendering the combined form here would show
              // the enquiry's fields twice, once under the wrong heading.
              // readOnly is applied by the wrapper.
              path: 'child/:childId/intake',
              element: (
                <RoleGuard allowedRoles={['therapist']}>
                  <StudentRegistrationProvider>
                    <HydratedReadOnlyForm>
                      <InclusionHeadIntakeForm />
                    </HydratedReadOnlyForm>
                  </StudentRegistrationProvider>
                </RoleGuard>
              )
            },
            {
              path: 'child/:childId/iep',
              element: (
                <RoleGuard allowedRoles={['therapist']}>
                  <StudentRegistrationProvider>
                    <HydratedReadOnlyForm>
                      <InclusionHeadIEP />
                    </HydratedReadOnlyForm>
                  </StudentRegistrationProvider>
                </RoleGuard>
              )
            },
            {
              path: 'child/:childId/iep/new',
              element: (
                <RoleGuard allowedRoles={['therapist']}>
                  <IepVersionEditor personaBasePath="/therapist" mode="new" />
                </RoleGuard>
              )
            },
            {
              path: 'child/:childId/iep/version/:versionId',
              element: (
                <RoleGuard allowedRoles={['therapist']}>
                  <IepVersionEditor personaBasePath="/therapist" mode="view" />
                </RoleGuard>
              )
            },
            {
              path: 'child/:childId/iep/version/:versionId/edit',
              element: (
                <RoleGuard allowedRoles={['therapist']}>
                  <IepVersionEditor personaBasePath="/therapist" mode="edit" />
                </RoleGuard>
              )
            }
          ]
        },
        {
          path: 'supervisor',
          children: [
            {
              path: 'dashboard',
              element: (
                <RoleGuard allowedRoles={['supervisor', 'admin']}>
                  <SupervisorDashboard />
                </RoleGuard>
              )
            },
            {
              path: 'calendar',
              element: (
                <RoleGuard allowedRoles={['supervisor', 'admin', 'ayro_it_admin', 'ayro_admin', 'org_admin']}>
                  <SupervisorCalendar />
                </RoleGuard>
              )
            },
            {
              path: 'attendance',
              element: (
                <RoleGuard allowedRoles={['supervisor', 'admin']}>
                  <SupervisorAttendance />
                </RoleGuard>
              )
            },
            {
              path: 'child',
              element: (
                <RoleGuard allowedRoles={['supervisor', 'admin', 'ayro_it_admin', 'ayro_admin', 'org_admin']}>
                  <SupervisorChild />
                </RoleGuard>
              )
            },
            {
              path: 'child/:childId',
              element: (
                <RoleGuard allowedRoles={['supervisor', 'admin', 'ayro_it_admin', 'ayro_admin', 'org_admin']}>
                  <SupervisorChildDetail />
                </RoleGuard>
              )
            },
            {
              path: 'child/:childId/:tab',
              element: (
                <RoleGuard allowedRoles={['supervisor', 'admin', 'ayro_it_admin', 'ayro_admin', 'org_admin']}>
                  <SupervisorChildDetail />
                </RoleGuard>
              )
            },
            {
              path: 'child/:childId/iep/new',
              element: (
                <RoleGuard allowedRoles={['supervisor', 'admin']}>
                  <IepVersionEditor personaBasePath="/supervisor" mode="new" />
                </RoleGuard>
              )
            },
            {
              path: 'child/:childId/iep/version/:versionId',
              element: (
                <RoleGuard allowedRoles={['supervisor', 'admin']}>
                  <IepVersionEditor personaBasePath="/supervisor" mode="view" />
                </RoleGuard>
              )
            },
            {
              path: 'child/:childId/iep/version/:versionId/edit',
              element: (
                <RoleGuard allowedRoles={['supervisor', 'admin']}>
                  <IepVersionEditor personaBasePath="/supervisor" mode="edit" />
                </RoleGuard>
              )
            },
            {
              path: 'settings',
              element: (
                <RoleGuard allowedRoles={['supervisor', 'admin']}>
                  <SupervisorSettings />
                </RoleGuard>
              )
            },
            {
              path: 'reports',
              element: (
                <RoleGuard allowedRoles={['supervisor', 'admin']}>
                  <SupervisorReports />
                </RoleGuard>
              )
            },
            {
              path: 'therapists',
              element: (
                <RoleGuard allowedRoles={['supervisor', 'admin', 'ayro_it_admin', 'ayro_admin', 'org_admin']}>
                  <SupervisorTherapists />
                </RoleGuard>
              )
            },
            {
              // Supervisor Profiles - same shape as Therapist Profiles: a directory,
              // then one supervisor with the children allocated to them.
              path: 'supervisors',
              element: (
                <RoleGuard allowedRoles={['supervisor', 'admin', 'ayro_it_admin', 'ayro_admin', 'org_admin']}>
                  <SupervisorSupervisors />
                </RoleGuard>
              )
            },
            {
              path: 'supervisors/:supervisorId',
              element: (
                <RoleGuard allowedRoles={['supervisor', 'admin', 'ayro_it_admin', 'ayro_admin', 'org_admin']}>
                  <SupervisorSupervisorDetail />
                </RoleGuard>
              )
            },
            {
              path: 'therapists/:id',
              element: (
                <RoleGuard allowedRoles={['supervisor', 'admin', 'ayro_it_admin', 'ayro_admin', 'org_admin']}>
                  <SupervisorTherapistDetail />
                </RoleGuard>
              )
            },
            {
              path: 'therapists/:id/:tab',
              element: (
                <RoleGuard allowedRoles={['supervisor', 'admin', 'ayro_it_admin', 'ayro_admin', 'org_admin']}>
                  <SupervisorTherapistDetail />
                </RoleGuard>
              )
            },
            {
              path: 'skills',
              element: (
                <RoleGuard allowedRoles={['supervisor', 'admin']}>
                  <SupervisorSkills />
                </RoleGuard>
              )
            },
            {
              path: 'materials',
              element: (
                <RoleGuard allowedRoles={['supervisor', 'admin']}>
                  <SupervisorMaterials />
                </RoleGuard>
              )
            },
            {
              path: 'form-builder',
              element: (
                <RoleGuard allowedRoles={['supervisor', 'admin']}>
                  <SupervisorFormBuilder />
                </RoleGuard>
              )
            },
            {
              path: 'form-builder/:templateId',
              element: (
                <RoleGuard allowedRoles={['supervisor', 'admin']}>
                  <SupervisorFormBuilder />
                </RoleGuard>
              )
            },
            {
              path: 'my-calendar',
              element: (
                <RoleGuard allowedRoles={['supervisor', 'admin']}>
                  <SupervisorCalendar />
                </RoleGuard>
              )
            },
            // Data collection for a session the supervisor started. Three paths,
            // matching the therapist and inclusion-head personas: bare (pick a
            // child), with a child, and with a child plus a datasheet tab.
            {
              path: 'data-collection',
              element: (
                <RoleGuard allowedRoles={['supervisor', 'admin']}>
                  <SupervisorDataCollection />
                </RoleGuard>
              )
            },
            {
              path: 'data-collection/:childId',
              element: (
                <RoleGuard allowedRoles={['supervisor', 'admin']}>
                  <SupervisorDataCollection />
                </RoleGuard>
              )
            },
            {
              path: 'data-collection/:childId/:type',
              element: (
                <RoleGuard allowedRoles={['supervisor', 'admin']}>
                  <SupervisorDataCollection />
                </RoleGuard>
              )
            },
            {
              path: 'add-student',
              element: (
                // Add New Child is grant-gated, not persona-gated: ANY role granted the "Add
                // New Child" page (add_student) in Role Definitions may register a child.
                // `allowedCodes={['add_student']}` is the real gate — it opens the route to
                // exactly the roles the entry card is shown to (the card gates on the same
                // granted_codes). The legacy `allowedRoles` list is kept only as a fallback so
                // the built-in personas still work if the catalogue has not been seeded; it no
                // longer bounces a centre/org admin or a custom role that has been granted the
                // page. add_student is reached from a card, not the sidebar, so it is not in
                // grantedUrls — which is why the code-based gate is needed here.
                <RoleGuard allowedRoles={['supervisor', 'admin', 'therapist']} allowedCodes={['add_student']}>
                  <StudentRegistrationProvider>
                    <Outlet />
                  </StudentRegistrationProvider>
                </RoleGuard>
              ),
              children: [
                {
                  index: true,
                  element: <SupervisorAddStudent />
                },
                {
                  // Separate again. The two were merged because 17 fields were
                  // duplicated between them and had to be kept in step; they are
                  // now split with those duplicates REMOVED from the intake, so
                  // each answer has exactly one editor and there is nothing to
                  // synchronise. The split also matches how the work is actually
                  // divided: an org admin takes the enquiry and hands the child
                  // to a supervisor, who completes the intake.
                  path: 'enquiry-form',
                  element: <InclusionHeadEnquiryForm />
                },
                {
                  // No `merged`: the intake renders its own fields only. The 17 the
                  // enquiry owns are hidden here rather than shown locked.
                  path: 'intake-form',
                  element: <InclusionHeadIntakeForm />
                },
                {
                  path: 'iep',
                  element: <InclusionHeadIEP />
                },
                {
                  path: 'dynamic-form/:templateId',
                  element: <SupervisorDynamicForm />
                }
              ]
            }
          ]
        },
        {
          path: 'admin',
          children: [
            {
              path: 'dashboard',
              element: (
                <RoleGuard allowedRoles={['super_admin', 'admin', 'ayro_it_admin', 'ayro_admin', 'org_admin']}>
                  <AdminDashboard />
                </RoleGuard>
              )
            },
            {
              // Placeholder for admin screens that are in the menu but not built
              // yet. One route, one param — each pending menu item points at its
              // own id so the sidebar highlights correctly. The page carries no
              // data, so every admin level may reach it; the menu is what decides
              // which ids a given level is ever offered.
              path: 'under-development/:feature',
              element: (
                <RoleGuard allowedRoles={['super_admin', 'admin', 'ayro_it_admin', 'ayro_admin', 'org_admin']}>
                  <AdminUnderDevelopment />
                </RoleGuard>
              )
            },
            {
              // All three admin levels reach this; the API decides what tree
              // each of them gets back, so an organization administrator lands
              // on their own organization as the root.
              // Readable by any admin (L1 gets a view-only banner); every write
              // is refused by the API for anyone but a platform administrator.
              path: 'features-pages',
              element: (
                <RoleGuard allowedRoles={['super_admin', 'admin', 'ayro_it_admin', 'ayro_admin']}>
                  <AdminFeaturesPages />
                </RoleGuard>
              )
            },
            {
              // Readable by all three levels; the API returns editable=false
              // for anyone but a platform administrator and refuses the write.
              path: 'role-definitions',
              element: (
                <RoleGuard allowedRoles={['super_admin', 'admin', 'ayro_it_admin', 'ayro_admin', 'org_admin']}>
                  <AdminRoleDefinitions />
                </RoleGuard>
              )
            },
            {
              // Reference only, so all three levels read it — an organization
              // administrator needs to see what a therapist gets as much as a
              // platform one does.
              path: 'access-matrix',
              element: (
                <RoleGuard allowedRoles={['super_admin', 'admin', 'ayro_it_admin', 'ayro_admin', 'org_admin']}>
                  <AdminAccessMatrix />
                </RoleGuard>
              )
            },
            {
              path: 'organizations-centers',
              element: (
                <RoleGuard allowedRoles={['super_admin', 'admin', 'ayro_it_admin', 'ayro_admin', 'org_admin']}>
                  <AdminOrganizationsCenters />
                </RoleGuard>
              )
            },
            // org_admin included: an organization administrator authors the forms
            // and workflows for their OWN organization. Both APIs are already
            // org-scoped (form_builder list/create go through Depends(get_org_id)
            // and FormTemplateRepository.get_by_org), so this widens who may open
            // the screen without widening what any one of them can see.
            // Whether the item appears at all is decided by the organization's
            // product tier — see menu-items/adminLevels.js.
            {
              path: 'form-builder',
              element: (
                <RoleGuard allowedRoles={['super_admin', 'admin', 'ayro_it_admin', 'ayro_admin', 'org_admin']}>
                  <SupervisorFormBuilder />
                </RoleGuard>
              )
            },
            {
              path: 'form-builder/:templateId',
              element: (
                <RoleGuard allowedRoles={['super_admin', 'admin', 'ayro_it_admin', 'ayro_admin', 'org_admin']}>
                  <SupervisorFormBuilder />
                </RoleGuard>
              )
            },
            {
              path: 'workflow-builder',
              element: (
                <RoleGuard allowedRoles={['super_admin', 'admin', 'ayro_it_admin', 'ayro_admin', 'org_admin']}>
                  <AdminWorkflowBuilder />
                </RoleGuard>
              )
            },
            {
              path: 'components',
              element: (
                <RoleGuard allowedRoles={['super_admin', 'admin', 'ayro_it_admin']}>
                  <AdminComponents />
                </RoleGuard>
              )
            },
            {
              // Skills Repository under the admin namespace (reuses the
              // supervisor component). Admin acts as Clinical Head and also owns
              // bulk import (import UI ships in a later slice).
              path: 'skills',
              element: (
                <RoleGuard allowedRoles={['super_admin', 'admin', 'ayro_it_admin', 'ayro_admin', 'org_admin']}>
                  <SupervisorSkills />
                </RoleGuard>
              )
            },
            {
              path: 'materials',
              element: (
                <RoleGuard allowedRoles={['super_admin', 'admin', 'ayro_it_admin', 'ayro_admin', 'org_admin']}>
                  <SupervisorMaterials />
                </RoleGuard>
              )
            },
            {
              path: 'user-management',
              element: (
                <RoleGuard allowedRoles={['super_admin', 'admin', 'ayro_it_admin', 'ayro_admin', 'org_admin']}>
                  <AdminUserManagement />
                </RoleGuard>
              )
            },
            {
              // The mockup's guided Add user — same grant operation as Quick
              // add, laid out over five steps. Its own page rather than a
              // dialog because the review step needs the room.
              path: 'user-management/add',
              element: (
                <RoleGuard allowedRoles={['super_admin', 'admin', 'ayro_it_admin', 'ayro_admin', 'org_admin']}>
                  <AdminAddUser />
                </RoleGuard>
              )
            },
            {
              // Product tier packaging. All admin personas may view; the page
              // itself is read-only for everyone but Ayro IT admin (and the
              // backend enforces the same on writes).
              path: 'product-tiers',
              element: (
                <RoleGuard allowedRoles={['super_admin', 'admin', 'ayro_it_admin', 'ayro_admin', 'org_admin']}>
                  <AdminProductTiers />
                </RoleGuard>
              )
            },
            {
              path: 'pages/select-center',
              element: (
                <RoleGuard allowedRoles={['super_admin', 'admin', 'ayro_it_admin']}>
                  <AdminSelectCenter />
                </RoleGuard>
              )
            },
            {
              path: 'pages/select-program',
              element: (
                <RoleGuard allowedRoles={['super_admin', 'admin', 'ayro_it_admin']}>
                  <AdminSelectProgram />
                </RoleGuard>
              )
            },
            {
              path: 'pages/select-grade',
              element: (
                <RoleGuard allowedRoles={['super_admin', 'admin', 'ayro_it_admin']}>
                  <AdminSelectGrade />
                </RoleGuard>
              )
            },
            {
              path: 'pages/coming-soon',
              element: (
                <RoleGuard allowedRoles={['super_admin', 'admin', 'ayro_it_admin']}>
                  <AdminComingSoon />
                </RoleGuard>
              )
            },
            {
              path: 'forms/center-time-session-notes',
              element: (
                <RoleGuard allowedRoles={['super_admin', 'admin', 'ayro_it_admin', 'ayro_admin']}>
                  <AdminCenterTimeSessionNotes />
                </RoleGuard>
              )
            },
            {
              path: 'forms/adolescence-session-notes',
              element: (
                <RoleGuard allowedRoles={['super_admin', 'admin', 'ayro_it_admin', 'ayro_admin']}>
                  <AdminAdolescenceSessionNotes />
                </RoleGuard>
              )
            },
            {
              path: 'forms/learning-support-session-summary',
              element: (
                <RoleGuard allowedRoles={['super_admin', 'admin', 'ayro_it_admin', 'ayro_admin']}>
                  <AdminLearningSupportSessionSummary />
                </RoleGuard>
              )
            },
            {
              path: 'forms/:formSlug',
              element: (
                <RoleGuard allowedRoles={['super_admin', 'admin', 'ayro_it_admin', 'ayro_admin']}>
                  <AdminFormPage />
                </RoleGuard>
              )
            }
          ]
        },
        {
          path: 'inclusionhead',
          element: (
            <StudentRegistrationProvider>
              <Outlet />
            </StudentRegistrationProvider>
          ),
          children: [
            {
              path: 'dashboard',
              element: (
                <RoleGuard allowedRoles={['inclusionhead']}>
                  <InclusionHeadDashboard />
                </RoleGuard>
              )
            },
            {
              path: 'child-profiles',
              element: (
                <RoleGuard allowedRoles={['inclusionhead']}>
                  <InclusionHeadChildProfiles />
                </RoleGuard>
              )
            },
            {
              path: 'child/:childId',
              element: (
                <RoleGuard allowedRoles={['inclusionhead']}>
                  <InclusionHeadWorkflowChildDetail />
                </RoleGuard>
              )
            },
            {
              path: 'registration',
              element: (
                <RoleGuard allowedRoles={['inclusionhead']}>
                  <InclusionHeadRegistration />
                </RoleGuard>
              )
            },
            {
              path: 'workflow-registration',
              element: (
                <RoleGuard allowedRoles={['inclusionhead']}>
                  <InclusionHeadWorkflowRegistration />
                </RoleGuard>
              )
            },
            {
              path: 'workflow-form/:templateId',
              element: (
                <RoleGuard allowedRoles={['inclusionhead']}>
                  <InclusionHeadWorkflowForm />
                </RoleGuard>
              )
            },
            {
              path: 'workflow-component/iep',
              element: (
                <RoleGuard allowedRoles={['inclusionhead']}>
                  <InclusionHeadWorkflowComponentIEP />
                </RoleGuard>
              )
            },
            {
              path: 'workflow-submission/:submissionId',
              element: (
                <RoleGuard allowedRoles={['inclusionhead']}>
                  <InclusionHeadWorkflowSubmissionView />
                </RoleGuard>
              )
            },
            {
              path: 'registration/enquiry-form',
              element: (
                <RoleGuard allowedRoles={['inclusionhead']}>
                  <InclusionHeadEnquiryForm />
                </RoleGuard>
              )
            },
            {
              path: 'registration/intake-form',
              element: (
                <RoleGuard allowedRoles={['inclusionhead']}>
                  <InclusionHeadIntakeForm />
                </RoleGuard>
              )
            },
            {
              path: 'registration/placement-assessment',
              element: (
                <RoleGuard allowedRoles={['inclusionhead']}>
                  <InclusionHeadPlacementAssessment />
                </RoleGuard>
              )
            },
            {
              path: 'registration/placement-interaction',
              element: (
                <RoleGuard allowedRoles={['inclusionhead']}>
                  <InclusionHeadPlacementInteraction />
                </RoleGuard>
              )
            },
            {
              path: 'registration/session-notes',
              element: (
                <RoleGuard allowedRoles={['inclusionhead']}>
                  <InclusionHeadSessionNotes />
                </RoleGuard>
              )
            },
            {
              path: 'registration/abc-analysis',
              element: (
                <RoleGuard allowedRoles={['inclusionhead']}>
                  <InclusionHeadAbcAnalysis />
                </RoleGuard>
              )
            },
            {
              path: 'registration/counsellor-observations',
              element: (
                <RoleGuard allowedRoles={['inclusionhead']}>
                  <InclusionHeadCounsellorObservations />
                </RoleGuard>
              )
            },
            {
              path: 'registration/parent-therapist-meeting',
              element: (
                <RoleGuard allowedRoles={['inclusionhead']}>
                  <InclusionHeadParentTherapistMeeting />
                </RoleGuard>
              )
            },
            {
              path: 'registration/anecdotal-record',
              element: (
                <RoleGuard allowedRoles={['inclusionhead']}>
                  <InclusionHeadAnecdotalRecord />
                </RoleGuard>
              )
            },
            {
              path: 'registration/student-referral',
              element: (
                <RoleGuard allowedRoles={['inclusionhead']}>
                  <InclusionHeadStudentReferral />
                </RoleGuard>
              )
            },
            {
              path: 'registration/placement-assessment-report',
              element: (
                <RoleGuard allowedRoles={['inclusionhead']}>
                  <InclusionHeadPlacementReport />
                </RoleGuard>
              )
            },
            {
              path: 'data-collection',
              element: (
                <RoleGuard allowedRoles={['inclusionhead']}>
                  <InclusionHeadDataCollection />
                </RoleGuard>
              )
            },
            {
              path: 'data-collection/:childId',
              element: (
                <RoleGuard allowedRoles={['inclusionhead']}>
                  <InclusionHeadDataCollection />
                </RoleGuard>
              )
            },
            {
              path: 'data-collection/:childId/:type',
              element: (
                <RoleGuard allowedRoles={['inclusionhead']}>
                  <InclusionHeadDataCollection />
                </RoleGuard>
              )
            },
            {
              path: 'my-calendar',
              element: (
                <RoleGuard allowedRoles={['inclusionhead']}>
                  <InclusionHeadMyCalendar />
                </RoleGuard>
              )
            },
            {
              path: 'reports',
              element: (
                <RoleGuard allowedRoles={['inclusionhead']}>
                  <SupervisorReports />
                </RoleGuard>
              )
            }
          ]
        },
        {
          // The caregiver's own screens. There is exactly one so far: the diary they
          // share with the care team. It lives here rather than under /supervisor
          // because a caregiver route may carry no child id — the server answers which
          // child is theirs — and a parameterless route is the only kind the sidebar can
          // link to. RoleGuard also admits any role whose GRANTS cover this path, which
          // is how a role cloned from Caregiver reaches it without being named here.
          path: 'caregiver',
          element: <Outlet />,
          children: [
            {
              // A bare /caregiver has no page of its own — without this it matched the
              // Outlet above with no child and rendered an empty shell. That is exactly
              // where closing the Communication Diary landed: its close button derives the
              // parent path by dropping the trailing slug, which for the caregiver's
              // /caregiver/communication-diary leaves /caregiver rather than a child
              // profile the way it does for a supervisor.
              index: true,
              element: <Navigate to="/caregiver/dashboard" replace />
            },
            {
              path: 'dashboard',
              element: (
                <RoleGuard allowedRoles={['parent']}>
                  <CaregiverDashboard />
                </RoleGuard>
              )
            },
            {
              // The caregiver's own child list. The only child directory they should ever
              // see: it reads /caregiver/children, which cannot return a child they are not
              // a recorded guardian of.
              path: 'child',
              element: (
                <RoleGuard allowedRoles={['parent']}>
                  <CaregiverChildren />
                </RoleGuard>
              )
            },
            {
              path: 'child/:childId',
              element: (
                <RoleGuard allowedRoles={['parent']}>
                  <CaregiverChildDetail />
                </RoleGuard>
              )
            },
            {
              // Deep link to one tab. Separate entry rather than an optional segment so a
              // bare /caregiver/child/:childId still resolves to the first visible tab
              // instead of 404ing on a missing param.
              path: 'child/:childId/:tab',
              element: (
                <RoleGuard allowedRoles={['parent']}>
                  <CaregiverChildDetail />
                </RoleGuard>
              )
            },
            {
              // The read-only IEP version view the IEP tab links to. mode="view" ONLY: the
              // `new` and `edit` routes the other personas mount are deliberately absent, so
              // a write path has nowhere to send a caregiver even if a control leaks into
              // the shared component. The API refuses the write regardless - the Caregiver
              // role holds read permissions only.
              path: 'child/:childId/iep/version/:versionId',
              element: (
                <RoleGuard allowedRoles={['parent']}>
                  <IepVersionEditor personaBasePath="/caregiver" mode="view" />
                </RoleGuard>
              )
            },
            {
              path: 'communication-diary',
              element: (
                <RoleGuard allowedRoles={['parent']}>
                  <CaregiverCommunicationDiary />
                </RoleGuard>
              )
            },
            {
              // Activity Center — a pending feature, so this serves the shared "Coming Soon"
              // placeholder. The sidebar item (menu-items/caregiver.js) is gated by the
              // activity_center grant; this route is what it links to. Swap in the real
              // screen when it is built.
              path: 'activity-center',
              element: (
                <RoleGuard allowedRoles={['parent']}>
                  <CaregiverComingSoon title="Activity Center" message="Home practice for your child is coming soon." />
                </RoleGuard>
              )
            },
            // Shareable clinical pages, reachable when granted to the Caregiver role in Role
            // Definitions. Same shared components every other persona uses; both are
            // repository CONTENT rather than anyone's personal data.
            {
              path: 'materials',
              element: (
                <RoleGuard allowedRoles={['parent']}>
                  <SupervisorMaterials />
                </RoleGuard>
              )
            },
            {
              path: 'skills',
              element: (
                <RoleGuard allowedRoles={['parent']}>
                  <SupervisorSkills />
                </RoleGuard>
              )
            }
          ]
        },
        {
          path: 'clinicalhead',
          element: <Outlet />,
          children: [
            {
              path: 'dashboard',
              element: (
                <RoleGuard allowedRoles={['clinicalhead']}>
                  <ClinicalHeadDashboard />
                </RoleGuard>
              )
            },
            {
              path: 'coming-soon',
              element: (
                <RoleGuard allowedRoles={['clinicalhead']}>
                  <ClinicalHeadComingSoon
                    title="More Features Coming Soon"
                    message="Additional clinical head tools are under development"
                  />
                </RoleGuard>
              )
            }
          ]
        },
        {
          path: 'widget',
          children: [
            {
              path: 'statistics',
              element: <WidgetStatistics />
            },
            {
              path: 'data',
              element: <WidgetData />
            },
            {
              path: 'chart',
              element: <WidgetChart />
            }
          ]
        },
        {
          path: 'apps',
          children: [
            {
              path: 'chat',
              element: <AppChat />
            },
            {
              path: 'calendar',
              element: <AppCalendar />
            },
            {
              path: 'kanban',
              element: <AppKanban />,
              children: [
                {
                  path: 'backlogs',
                  element: <AppKanbanBacklogs />
                },
                {
                  path: 'board',
                  element: <AppKanbanBoard />
                }
              ]
            },
            {
              path: 'customer',
              children: [
                {
                  path: 'customer-list',
                  element: <AppCustomerList />
                },
                {
                  path: 'customer-card',
                  element: <AppCustomerCard />
                }
              ]
            },
            {
              path: 'invoice',
              children: [
                {
                  path: 'dashboard',
                  element: <AppInvoiceDashboard />
                },
                {
                  path: 'create',
                  element: <AppInvoiceCreate />
                },
                {
                  path: 'details/:id',
                  element: <AppInvoiceDetails />
                },
                {
                  path: 'edit/:id',
                  element: <AppInvoiceEdit />
                },
                {
                  path: 'list',
                  element: <AppInvoiceList />
                }
              ]
            },
            {
              path: 'profiles',
              children: [
                {
                  path: 'account',
                  element: <AccountProfile />,
                  children: [
                    {
                      path: 'basic',
                      element: <AccountTabProfile />
                    },
                    {
                      path: 'personal',
                      element: <AccountTabPersonal />
                    },
                    {
                      path: 'my-account',
                      element: <AccountTabAccount />
                    },
                    {
                      path: 'password',
                      element: <AccountTabPassword />
                    },
                    {
                      path: 'role',
                      element: <AccountTabRole />
                    },
                    {
                      path: 'settings',
                      element: <AccountTabSettings />
                    }
                  ]
                },
                {
                  path: 'user',
                  element: <UserProfile />,
                  children: [
                    {
                      path: 'personal',
                      element: <UserTabPersonal />
                    },
                    {
                      path: 'payment',
                      element: <UserTabPayment />
                    },
                    {
                      path: 'password',
                      element: <UserTabPassword />
                    },
                    {
                      path: 'settings',
                      element: <UserTabSettings />
                    }
                  ]
                }
              ]
            },
            {
              path: 'e-commerce',
              children: [
                {
                  path: 'products',
                  element: <AppECommProducts />,
                  loader: productsLoader,
                  errorElement: <ErrorBoundary />
                },
                {
                  path: 'product-details/:id',
                  element: <AppECommProductDetails />,
                  loader: productLoader,
                  errorElement: <ErrorBoundary />
                },
                {
                  path: 'product-list',
                  element: <AppECommProductList />,
                  loader: productsLoader,
                  errorElement: <ErrorBoundary />
                },
                {
                  path: 'add-new-product',
                  element: <AppECommAddProduct />
                },
                {
                  path: 'checkout',
                  element: <AppECommCheckout />
                }
              ]
            }
          ]
        },
        {
          path: 'forms',
          children: [
            {
              path: 'validation',
              element: <FormsValidation />
            },
            {
              path: 'wizard',
              element: <FormsWizard />
            },
            {
              path: 'layout',
              children: [
                {
                  path: 'basic',
                  element: <FormsLayoutBasic />
                },
                {
                  path: 'multi-column',
                  element: <FormsLayoutMultiColumn />
                },
                {
                  path: 'action-bar',
                  element: <FormsLayoutActionBar />
                },
                {
                  path: 'sticky-bar',
                  element: <FormsLayoutStickyBar />
                }
              ]
            },
            {
              path: 'plugins',
              children: [
                {
                  path: 'mask',
                  element: <FormsPluginsMask />
                },
                {
                  path: 'clipboard',
                  element: <FormsPluginsClipboard />
                },
                {
                  path: 're-captcha',
                  element: <FormsPluginsRecaptcha />
                },
                {
                  path: 'editor',
                  element: <FormsPluginsEditor />
                },
                {
                  path: 'dropzone',
                  element: <FormsPluginsDropzone />
                }
              ]
            }
          ]
        },
        {
          path: 'tables',
          children: [
            {
              path: 'react-table',
              children: [
                {
                  path: 'basic',
                  element: <ReactTableBasic />
                },
                {
                  path: 'dense',
                  element: <ReactDenseTable />
                },
                {
                  path: 'sorting',
                  element: <ReactTableSorting />
                },
                {
                  path: 'filtering',
                  element: <ReactTableFiltering />
                },
                {
                  path: 'grouping',
                  element: <ReactTableGrouping />
                },
                {
                  path: 'pagination',
                  element: <ReactTablePagination />
                },
                {
                  path: 'row-selection',
                  element: <ReactTableRowSelection />
                },
                {
                  path: 'expanding',
                  element: <ReactTableExpanding />
                },
                {
                  path: 'editable',
                  element: <ReactTableEditable />
                },
                {
                  path: 'drag-drop',
                  element: <ReactTableDragDrop />
                },
                {
                  path: 'column-visibility',
                  element: <ReactTableColumnVisibility />
                },
                {
                  path: 'column-resizing',
                  element: <ReactTableColumnResizing />
                },
                {
                  path: 'sticky-table',
                  element: <ReactTableStickyTable />
                },
                {
                  path: 'umbrella',
                  element: <ReactTableUmbrella />
                },
                {
                  path: 'empty',
                  element: <ReactTableEmpty />
                },
                {
                  path: 'virtualized',
                  element: <ReactTableVirtualized />
                }
              ]
            },
            {
              path: 'mui-table',
              children: [
                {
                  path: 'basic',
                  element: <MuiTableBasic />
                },
                {
                  path: 'dense',
                  element: <MuiTableDense />
                },
                {
                  path: 'enhanced',
                  element: <MuiTableEnhanced />
                },
                {
                  path: 'datatable',
                  element: <MuiTableDatatable />
                },
                {
                  path: 'custom',
                  element: <MuiTableCustom />
                },
                {
                  path: 'fixed-header',
                  element: <MuiTableFixedHeader />
                },
                {
                  path: 'collapse',
                  element: <MuiTableCollapse />
                }
              ]
            }
          ]
        },
        {
          path: 'charts',
          children: [
            {
              path: 'apexchart',
              element: <ChartApexchart />
            },
            {
              path: 'org-chart',
              element: <ChartOrganization />
            }
          ]
        },
        {
          path: 'map',
          element: <Map />
        },
        {
          path: 'sample-page',
          element: <SamplePage />
        },
        {
          path: 'pricing',
          element: <PricingPage />
        }
      ]
    },
    {
      path: '/maintenance',
      element: <PagesLayout />,
      children: [
        {
          path: '404',
          element: <MaintenanceError />
        },
        {
          path: '500',
          element: <MaintenanceError500 />
        },
        {
          path: 'under-construction',
          element: <MaintenanceUnderConstruction />
        },
        {
          path: 'coming-soon',
          element: <MaintenanceComingSoon />
        }
      ]
    },
    {
      path: '/auth',
      element: <PagesLayout />,
      children: [
        {
          path: 'login',
          element: <AuthLogin />
        },
        {
          path: 'register',
          element: <AuthRegister />
        },
        {
          path: 'forgot-password',
          element: <AuthForgotPassword />
        },
        {
          path: 'reset-password',
          element: <AuthResetPassword />
        },
        {
          path: 'check-mail',
          element: <AuthCheckMail />
        },
        {
          path: 'code-verification',
          element: <AuthCodeVerification />
        }
      ]
    },
    {
      path: '/',
      element: <SimpleLayout layout={SimpleLayoutType.SIMPLE} />,
      children: [
        {
          path: 'contact-us',
          element: <AppContactUS />
        }
      ]
    }
  ]
};

export default MainRoutes;
